import React from 'react';
import NavBar from '../components/Navbar/NavBar';
import Footer from '../components/Footer';
import { useDocTitle } from '../components/CustomHook';
import { Link } from 'react-router-dom';
import img3 from '../images/investor-partnerships.jpg';

const InvestorPartnerships = () => {
    useDocTitle('Investor Partnerships - TerraLucrum');

    return (
        <>
            <NavBar />
            <div className="bg-gray-50 min-h-screen pt-28">
                {/* Hero Section */}
                <div className="bg-terra-blue-900 text-white py-20">
                    <div className="container mx-auto px-4 text-center">
                        <h1 className="text-4xl lg:text-6xl font-bold mb-4">Investor Partnerships</h1>
                        <p className="text-xl lg:text-2xl mb-8">Partner with us to build passive wealth through real estate</p>
                        <Link to="/contact" className="bg-white text-terra-blue-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition duration-300">
                            Become an Investor
                        </Link>
                    </div>
                </div>

                {/* Main Content */}
                <div className="container mx-auto px-4 py-16">
                    <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
                        <div>
                            <img src={img3} alt="Investor Partnerships" className="w-full max-w-md mx-auto" />
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold text-terra-blue-900 mb-6">Invest Alongside Us</h2>
                            <p className="text-lg text-gray-700 mb-6">
                                TerraLucrum gives investors access to institutional-quality real estate opportunities that
                                are typically out of reach for individuals. We source, underwrite, and manage every deal so
                                you can earn passive income and share in long-term appreciation without the day-to-day work
                                of being a landlord.
                            </p>
                            <p className="text-lg text-gray-700">
                                Whether you are looking to diversify beyond stocks and bonds or put idle capital to work in
                                income-producing property, our partnership structures let you invest at the level that fits
                                your goals. We handle acquisitions, operations, and reporting while you build wealth on
                                autopilot.
                            </p>
                        </div>
                    </div>

                    {/* Leadership Approach */}
                    <div className="mb-16">
                        <h2 className="text-3xl font-bold text-terra-blue-900 text-center mb-12">Why Partner With TerraLucrum</h2>
                        <div className="grid md:grid-cols-3 gap-8">
                            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                                <div className="bg-terra-blue-900 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 1L9 9l-8 0 6.5 4.7L5 22l7-5 7 5-2.5-8.3L23 9l-8 0z"/>
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-terra-blue-900 mb-4">Aligned Interests</h3>
                                <p className="text-gray-600">We invest our own capital in every deal, so we only succeed when our partners succeed</p>
                            </div>
                            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                                <div className="bg-terra-blue-900 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-terra-blue-900 mb-4">Transparent Reporting</h3>
                                <p className="text-gray-600">Clear, regular updates on property performance, distributions, and returns you can trust</p>
                            </div>
                            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                                <div className="bg-terra-blue-900 text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 1v6h6L8.5 5.5 10 4h5v2h-3l1 1 3.25 3.25c.31.31.69.5 1.06.69L18 12h4v8c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h6L8.5 2.5 4 1z"/>
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-terra-blue-900 mb-4">Long-Term Value</h3>
                                <p className="text-gray-600">A patient, disciplined strategy focused on durable cash flow and lasting appreciation</p>
                            </div>
                        </div>
                    </div>

                    {/* Services Section */}
                    <div className="mb-16">
                        <h2 className="text-3xl font-bold text-terra-blue-900 text-center mb-12">Partnership Opportunities</h2>
                        <div className="grid md:grid-cols-2 gap-8">
                            <div className="bg-white p-8 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-semibold text-terra-blue-900 mb-6">Equity Partnerships</h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Direct ownership stakes in income-producing property</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Pro-rata share of cash flow and appreciation</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Preferred return structures for investors</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Tax advantages of direct real estate ownership</li>
                                </ul>
                            </div>
                            <div className="bg-white p-8 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-semibold text-terra-blue-900 mb-6">Passive Investing</h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Fully hands-off investment opportunities</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Regular income distributions</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Professional acquisition and asset management</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Diversification across markets and asset types</li>
                                </ul>
                            </div>
                            <div className="bg-white p-8 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-semibold text-terra-blue-900 mb-6">Joint Ventures</h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Co-investment on larger acquisitions</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Shared capital and shared upside</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Value-add and repositioning projects</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Flexible partnership terms and structures</li>
                                </ul>
                            </div>
                            <div className="bg-white p-8 rounded-lg shadow-lg">
                                <h3 className="text-2xl font-semibold text-terra-blue-900 mb-6">1031 Exchange Opportunities</h3>
                                <ul className="space-y-3 text-gray-700">
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Defer capital gains on the sale of property</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Identify qualified replacement properties</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Roll equity into professionally managed assets</li>
                                    <li className="flex items-start"><span className="text-terra-blue-900 mr-2">•</span> Coordination with qualified intermediaries</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Differentiators Section */}
                    <div className="bg-white rounded-lg p-8 shadow-lg mb-16">
                        <h2 className="text-3xl font-bold text-terra-blue-900 text-center mb-12">Why Invest With TerraLucrum?</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            <div className="text-center">
                                <h3 className="font-semibold text-terra-blue-900 mb-4">Proven Track Record</h3>
                                <p className="text-gray-600">A history of acquiring and operating properties that deliver consistent returns</p>
                            </div>
                            <div className="text-center">
                                <h3 className="font-semibold text-terra-blue-900 mb-4">Disciplined Underwriting</h3>
                                <p className="text-gray-600">Conservative assumptions and rigorous analysis on every deal we bring to investors</p>
                            </div>
                            <div className="text-center">
                                <h3 className="font-semibold text-terra-blue-900 mb-4">Investor-First Approach</h3>
                                <p className="text-gray-600">Structures designed to protect capital and prioritize partner returns</p>
                            </div>
                            <div className="text-center">
                                <h3 className="font-semibold text-terra-blue-900 mb-4">Full Transparency</h3>
                                <p className="text-gray-600">Open communication and detailed reporting at every stage of the investment</p>
                            </div>
                        </div>
                    </div>

                    {/* Investor Returns
                    <div className="bg-terra-blue-50 rounded-lg p-8 mb-16">
                        <h2 className="text-3xl font-bold text-terra-blue-900 text-center mb-12">Investor Performance</h2>
                        <div className="grid md:grid-cols-3 gap-8 text-center">
                            <div>
                                <div className="text-4xl font-bold text-terra-blue-900 mb-2">8%</div>
                                <p className="text-gray-600">Average preferred return to investors</p>
                            </div>
                            <div>
                                <div className="text-4xl font-bold text-terra-blue-900 mb-2">$50M+</div>
                                <p className="text-gray-600">Real estate assets under management</p>
                            </div>
                            <div>
                                <div className="text-4xl font-bold text-terra-blue-900 mb-2">100%</div>
                                <p className="text-gray-600">Of properties acquired with investor capital</p>
                            </div>
                        </div>
                    </div>
                        */}
                    {/* CTA Section */}
                    <div className="text-center bg-terra-blue-900 text-white p-12 rounded-lg">
                        <h2 className="text-3xl font-bold mb-4">Ready to Start Investing?</h2>
                        <p className="text-xl mb-8">Connect with our team to explore partnership opportunities that fit your goals.</p>
                        <Link to="/contact" className="bg-white text-terra-blue-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition duration-300">
                            Contact Us
                        </Link>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default InvestorPartnerships;
